/**
 * Theme color for grading components (brand orange)
 */
export const THEME_COLOR = '#f56600';

// ============================================
// GRADING LIST TYPES
// ============================================

/**
 * Status of a grading session
 */
export type GradingStatus = 'draft' | 'in_progress' | 'completed';

/**
 * Grading list item for dashboard display
 */
export interface GradingListItem {
  id: string;
  topic: string | null;
  status: GradingStatus;
  studentCount: number;
  averagePercentage: number | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Pagination metadata
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Response for listing gradings
 */
export interface GradingListResponse {
  items: GradingListItem[];
  pagination: PaginationMeta;
}

/**
 * Role of a chat message sender
 */
export enum ChatMessageRole {
  USER = 'user',
  ASSISTANT = 'assistant',
}

/**
 * Status of a tool call execution
 */
export enum ToolCallStatus {
  PENDING = 'pending',
  SUCCESS = 'success',
  ERROR = 'error',
}

/**
 * Status of a chat message (for streaming support)
 */
export enum ChatMessageStatus {
  /** Message is being generated or tool is being executed */
  LOADING = 'loading',
  /** Message generation is complete */
  DONE = 'done',
  /** Message generation failed */
  ERROR = 'error',
}

/**
 * AI context from tool execution - stored for LLM continuity
 */
export interface ToolAiContext {
  /** Human-readable summary of what happened */
  summary: string;
  /** Structured key facts the LLM can reference */
  keyFacts: Record<string, unknown>;
}

/**
 * Standard tool output structure - all tools return this shape
 */
export interface StandardToolOutput {
  /** Context for the AI/LLM */
  aiContext: ToolAiContext;
  /** Entity IDs for frontend to fetch */
  entities: Record<string, string>;
}

/**
 * Tool call result
 */
export interface ToolCallResult {
  raw: StandardToolOutput;
  summary?: string;
}

/**
 * Tool call made by assistant
 */
export interface ToolCall {
  id: string;
  name: string;
  input: Record<string, unknown>;
  result?: ToolCallResult;
  status: ToolCallStatus;
  error?: string;
}

/**
 * Raw Claude API response for debugging
 */
export interface RawClaudeResponse {
  id: string;
  model: string;
  stopReason: string;
  usage: {
    inputTokens: number;
    outputTokens: number;
  };
  contentBlocks: unknown[];
}

/**
 * Resource processing status from backend
 */
export type ResourceProcessingStatus = 'pending' | 'processing' | 'completed' | 'failed';

/**
 * Resource response from API
 */
export interface Resource {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  signedUrl?: string;
  processingStatus?: ResourceProcessingStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * Resource reference in grading session
 */
export interface GradingResource {
  resourceId: string;
  pendingMessageAttachment: boolean;
  resource?: Resource;
}

/**
 * Chat message interface
 */
export interface ChatMessage {
  id: string;
  role: ChatMessageRole;
  content: string;
  resourceIds: string[];
  resources?: Resource[];
  toolCalls?: ToolCall[];
  groupId?: string;
  /** Message generation status for streaming support */
  status: ChatMessageStatus;
  rawClaudeResponse?: RawClaudeResponse;
  /** AI generation duration in milliseconds (assistant messages only) */
  generationDurationMs?: number;
  createdAt: Date;
}

/**
 * Send message response from API
 * Returns groupId for SSE streaming connection
 */
export interface SendMessageResponse {
  groupId: string;
}

/**
 * SSE event for message streaming
 */
export interface MessageStreamEvent {
  type: 'message' | 'done' | 'error' | 'entity_update';
  data?: ChatMessage;
  error?: string;
}

/**
 * Resource preview for display in materials analysis
 */
export interface ResourcePreview {
  id: string;
  name: string;
  mimeType: string;
  url: string;
}

/**
 * Student mapping in materials analysis preview
 */
export interface MaterialsAnalysisStudentMapping {
  studentId: string;
  firstName: string;
  lastName?: string;
  resourceIds: string[];
  resourcePreviews: ResourcePreview[];
  isNewStudent: boolean;
}

/**
 * Materials analysis preview data (from update_materials_analysis tool)
 */
export interface MaterialsAnalysisPreview {
  materials: {
    hasQuestions: boolean;
    hasAnswerKey: boolean;
    hasRubric: boolean;
    hasStudentWork: boolean;
    studentWorkCount: number;
  };
  studentMappings: MaterialsAnalysisStudentMapping[];
  unmappedResourceIds: string[];
  unmappedResourcePreviews: ResourcePreview[];
  missingRequired: string[];
  isReadyForGrading: boolean;
}


// ============================================
// SCORING GUIDE PREVIEW TYPES
// ============================================

/**
 * Scoring guide standard reference
 */
export interface ScoringGuideStandardRef {
  standardId: string;
  alignmentRationale: string;
}

/**
 * Scoring criteria level
 */
export interface ScoringCriteriaLevel {
  /** UUID */
  id: string;
  description: string;
  points: number;
}

/**
 * Scoring criteria
 */
export interface ScoringCriteriaPreview {
  id: string;
  name: string;
  standard?: ScoringGuideStandardRef;
  levels: ScoringCriteriaLevel[];
  order: number;
}

/**
 * Scoring item (question)
 */
export interface ScoringItemPreview {
  id: string;
  questionIdentifier: string;
  questionText: string;
  type: 'rubric' | 'logic' | 'exact_match';
  order: number;
  correctAnswer?: string;
  criteria: ScoringCriteriaPreview[];
}

/**
 * Scoring section
 */
export interface ScoringSectionPreview {
  id: string;
  title: string;
  items: ScoringItemPreview[];
  order: number;
}

/**
 * Scoring guide standard (full data)
 */
export interface ScoringGuideStandard {
  code: string;
  text: string;
}

/**
 * Scoring guide preview data (from generate_scoring_guide tool)
 */
export interface ScoringGuidePreview {
  scoringGuideId: string;
  scoringGuide: {
    topic: string;
    sections: ScoringSectionPreview[];
    standards: ScoringGuideStandard[];
  };
  summary: {
    totalQuestions: number;
    totalPoints: number;
    standardsAligned: number;
    itemTypes: {
      rubric: number;
      logic: number;
      exact_match: number;
    };
  };
}


/**
 * Student work mapping entry
 */
export interface StudentWorkMapping {
  studentId: string;
  resourceIds: string[];
}

/**
 * Materials analysis state stored on grading session
 */
export interface MaterialsAnalysis {
  hasQuestions: boolean;
  hasAnswerKey: boolean;
  hasRubric: boolean;
  hasStudentWork: boolean;
  questionResourceIds: string[];
  answerKeyResourceIds: string[];
  rubricResourceIds: string[];
  studentMappings: MaterialsAnalysisStudentMapping[];
  unmappedResourceIds: string[];
  notes?: string;
  updatedAt: Date;
}

/**
 * Grading session response from API
 */
export interface GradingSession {
  id: string;
  userId: string;
  messages: ChatMessage[];
  resources: GradingResource[];
  /** Materials analysis state (set after update_materials_analysis tool) */
  materialsAnalysis: MaterialsAnalysis | null;
  /** Student-to-work mappings (populated after student identification) */
  studentWorkMappings: StudentWorkMapping[];
  /** Current scoring guide ID */
  currentScoringGuideId: string | null;
  /** Latest grading run ID (null if no grading has been performed) */
  latestGradingRunId: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================
// GRADING RESULTS PREVIEW TYPES
// ============================================

/**
 * Grade distribution counts
 */
export interface GradeDistribution {
  gradeA: number;
  gradeB: number;
  gradeC: number;
  gradeD: number;
  gradeF: number;
}

/**
 * Common issue identified in student responses
 */
export interface CommonIssue {
  issue: string;
  affectedCount: number;
}

/**
 * Item statistics from grading run
 */
export interface ItemStats {
  itemId: string;
  questionIdentifier: string;
  questionText: string;
  totalStudents: number;
  averageScore: number;
  maxScore: number;
  gradeDistribution: GradeDistribution;
  commonIssues: CommonIssue[];
}

/**
 * Section statistics from grading run
 */
export interface SectionStats {
  sectionId: string;
  title: string;
  order: number;
  items: ItemStats[];
}

/**
 * Student result in a grading run
 */
export interface GradingRunStudentResult {
  studentId: string;
  studentName: string;
  gradingResultId: string;
  isNewInThisRun: boolean;
  status: 'success' | 'failed';
  /** Computed score with all calculated fields (null if grading failed) */
  score?: ComputedScore | null;
  failureReason?: string;
}

/**
 * Grading run response from API
 */
export interface GradingRun {
  id: string;
  gradingId: string;
  scoringGuideId: string;
  students: GradingRunStudentResult[];
  stats: {
    totalStudents: number;
    totalSuccessful: number;
    totalFailed: number;
    averagePercentage?: number;
    minPercentage?: number;
    maxPercentage?: number;
    gradeDistribution?: GradeDistribution;
  };
  sectionStats: SectionStats[];
  aiAnalysisGenerated: boolean;
  /** Question analysis for the grading session (included for convenience) */
  questionAnalysis?: QuestionAnalysis;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Common insight item (strength or area for improvement)
 */
export interface CommonInsight {
  text: string;
  studentCount: number;
}

/**
 * Grading results preview data (used by UI components)
 * This is derived from GradingRun response
 */
export interface GradingResultsPreview {
  gradingId: string;
  gradingRunId: string;
  studentCount: number;
  completedCount: number;
  failedCount: number;
  averagePercentage?: number;
  minPercentage?: number;
  maxPercentage?: number;
  gradeDistribution?: GradeDistribution;
  students: GradingRunStudentResult[];
  sectionStats: SectionStats[];
  aiAnalysisGenerated: boolean;
  /** Question analysis for the grading session (included for convenience) */
  questionAnalysis?: QuestionAnalysis;
  /** Common strengths observed across students */
  commonStrengths?: CommonInsight[];
  /** Common areas for improvement across students */
  commonAreasForImprovement?: CommonInsight[];
}


/**
 * Pending attachment for upload preview
 * Represents a file that has been selected/uploaded but not yet sent with a message
 */
export interface PendingAttachment {
  /** Local temporary ID for tracking */
  localId: string;
  /** Resource ID from backend (set after upload completes) */
  resourceId?: string;
  /** Original file name */
  fileName: string;
  /** File MIME type */
  mimeType: string;
  /** File size in bytes */
  size: number;
  /** Local preview URL (blob URL) */
  previewUrl?: string;
  /** Signed URL from backend (set after upload completes) */
  signedUrl?: string;
  /** Upload progress (0-100) */
  uploadProgress: number;
  /** Upload state */
  uploadState: 'pending' | 'uploading' | 'processing' | 'completed' | 'error';
  /** Error message if upload failed */
  errorMessage?: string;
}

// ============================================
// COMPUTED SCORE TYPES
// ============================================

/**
 * Base letter grades (A, B, C, D, F)
 */
export type GradeLetter = 'A' | 'B' | 'C' | 'D' | 'F';

/**
 * Letter grades with plus/minus modifiers (A+, A, A-, B+, B, B-, etc.)
 */
export type GradeLetterWithSign =
  | 'A+'
  | 'A'
  | 'A-'
  | 'B+'
  | 'B'
  | 'B-'
  | 'C+'
  | 'C'
  | 'C-'
  | 'D'
  | 'F';

/**
 * Computed score data - shared interface for criteria, item, section, and root levels.
 * Enables unified display components (badges, score displays, etc.)
 */
export interface ComputedScore {
  /** Points earned at this level */
  earnedPoints: number;
  /** Maximum points possible at this level */
  maxPoints: number;
  /** Percentage score (0-100, rounded) */
  percentage: number;
  /** Base letter grade (A, B, C, D, F) */
  gradeLetter: GradeLetter;
  /** Letter grade with plus/minus (A+, A, A-, B+, B, B-, C+, C, C-, D, F) */
  gradeLetterWithSign: GradeLetterWithSign;
}

// ============================================
// DETAILED GRADING RESULT TYPES
// ============================================

/**
 * Question pointer - position on student work image
 */
export interface QuestionPointer {
  /** X coordinate (0-1, left to right) */
  x: number;
  /** Y coordinate (0-1, top to bottom) */
  y: number;
  /** Page index (0-based) */
  pageIndex: number;
}

/**
 * Error pointer - marks errors on student work
 */
export interface ErrorPointer extends QuestionPointer {
  /** Error description */
  description: string;
  /** Type of issue */
  type: 'error' | 'warning';
}

/**
 * Criteria evaluation in a grading result (enriched with computed score)
 */
export interface CriteriaEvaluation {
  /** Criteria ID (matches scoring guide) */
  criteriaId: string;
  /** Awarded level UUID (matches CriteriaLevel.id) */
  awardedLevelId: string;
  /** Reasons why student met this level */
  levelMetBecause: string[];
  /** Reasons why student did not reach next level */
  nextLevelNotMetBecause: string[];
  /** Error locations on student work */
  errorPointers?: ErrorPointer[];
  /** Computed score for this criteria */
  score: ComputedScore;
}

/**
 * Item (question) evaluation in a grading result (enriched with computed score)
 */
export interface ItemEvaluation {
  /** Item ID (matches scoring guide) */
  itemId: string;
  /** Criteria evaluations */
  criteriaEvaluations: CriteriaEvaluation[];
  /** Feedback for this question */
  feedback: string;
  /** Location of answer on student work */
  questionPointer?: QuestionPointer | null;
  /** Computed score for this item (aggregate of all criteria) */
  score: ComputedScore;
}

/**
 * Section evaluation in a grading result (enriched with computed score)
 */
export interface SectionEvaluation {
  /** Section ID (matches scoring guide) */
  sectionId: string;
  /** Item evaluations */
  itemEvaluations: ItemEvaluation[];
  /** Computed score for this section (aggregate of all items) */
  score: ComputedScore;
}

/**
 * Detailed grading result for a student (enriched with computed scores)
 */
export interface GradingResultDetail {
  /** Grading result ID */
  id: string;
  /** Grading session ID */
  gradingId: string;
  /** Scoring guide ID */
  scoringGuideId: string;
  /** Student ID */
  studentId: string;
  /** Resource IDs of student work pages */
  resourceIds: string[];
  /** Status */
  status: 'in_progress' | 'completed' | 'failed';
  /** Section evaluations (enriched with computed scores) */
  sectionEvaluations: SectionEvaluation[];
  /** Overall summary feedback */
  overallFeedback: string;
  /** Strengths identified */
  strengths: string[];
  /** Areas for improvement */
  areasForImprovement: string[];
  /** Failure reason (if status is failed) */
  failureReason?: string;
  /** Question analysis for the grading session (included for convenience) */
  questionAnalysis?: QuestionAnalysis | null;
  /** Total computed score (aggregate of all sections) */
  score: ComputedScore;
  /** Created timestamp */
  createdAt: Date;
  /** Updated timestamp */
  updatedAt: Date;
}

// ============================================
// QUESTION ANALYSIS TYPES
// ============================================

/**
 * Grade distribution counts
 */
export interface GradeDistribution {
  /** A grade (90-100%) */
  gradeA: number;
  /** B grade (80-89%) */
  gradeB: number;
  /** C grade (70-79%) */
  gradeC: number;
  /** D grade (60-69%) */
  gradeD: number;
  /** F grade (0-59%) */
  gradeF: number;
}

/**
 * A single common issue with affected student count
 */
export interface CommonIssue {
  /** Description of the common issue/mistake */
  issue: string;
  /** Number of students who made this mistake (minimum 2) */
  affectedCount: number;
}

/**
 * Item analysis statistics
 */
export interface ItemAnalysis {
  /** Item ID (matches scoring guide) */
  itemId: string;
  /** Question identifier as shown on assignment (e.g., "1", "1a") */
  questionIdentifier: string;
  /** Question text */
  questionText: string;
  /** Total students who answered this question */
  totalStudents: number;
  /** Grade distribution counts */
  gradeDistribution: GradeDistribution;
  /** Average score for this item */
  averageScore: number;
  /** Maximum possible score for this item */
  maxScore: number;
  /** AI-generated common issues (only issues with 2+ affected students) */
  commonIssues: CommonIssue[];
}

/**
 * Section analysis statistics
 */
export interface SectionAnalysis {
  /** Section ID (matches scoring guide) */
  sectionId: string;
  /** Section title */
  title: string;
  /** Item statistics within this section */
  items: ItemAnalysis[];
  /** Order within the scoring guide */
  order: number;
}

/**
 * Complete question analysis for a grading session
 */
export interface QuestionAnalysis {
  /** Grading session ID */
  gradingId: string;
  /** Scoring guide ID */
  scoringGuideId: string;
  /** Grading run ID this analysis is based on */
  gradingRunId: string;
  /** Section-level statistics */
  sections: SectionAnalysis[];
  /** Total students included in analysis */
  totalStudents: number;
  /** Whether AI analysis has been generated */
  aiAnalysisGenerated: boolean;
  /** Created timestamp */
  createdAt: Date;
  /** Updated timestamp */
  updatedAt: Date;
}
